import { configuredFetchBaseQuery } from '@config/rtkQuery';
import { removeEmpty } from '@features/removeEmpty';
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

const paramsSerializer = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        searchParams.append(`${key}`, item);
      });
    } else {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

export const planApi = createApi({
  reducerPath: 'planApi',
  baseQuery: configuredFetchBaseQuery(paramsSerializer),
  endpoints: (builder) => ({
    getList: builder.query({
      query: (arg) => {
        const url = 'customer/plans';
        const { tos, country, type } = arg;
        return {
          url,
          params: removeEmpty({ tos, country, [`did-types`]: type }),
        };
      },
      transformResponse: (response) => response.results,
      providesTags: (result, error, { tos, country, type }) => [
        { tag: 'Plans', tos, country, didType: type },
      ],
    }),
    getForSelect: builder.query({
      query: ({ id }) => ({ url: `customer/plans/countries/${id}/for-select` }),
    }),
    getPSTNForSelect: builder.query({
      query: ({ id }) => ({
        url: `customer/plans/pstn/countries/${id}/for-select`,
      }),
    }),
  }),
});

export const planDetailsApi = createApi({
  reducerPath: 'planDetailsApi',
  baseQuery: configuredFetchBaseQuery(paramsSerializer),
  endpoints: (builder) => ({
    getPricing: builder.query({
      query: (arg) => {
        const { id, type } = arg;
        return { url: `customer/plans/${id}/type/${type}/pricing` };
      },
      transformResponse: (response, meta, { name }) => {
        const { id, non_usage: nonUsage, usage, contract } = response;
        return { id, nonUsage, usage, contract, name };
      },
      providesTags: (result, error, { id, type }) => [
        { tag: 'PlanPricing', id, type },
      ],
    }),
    getRequirements: builder.query({
      query: (arg) => {
        const { id, type } = arg;
        return { url: `customer/plans/${id}/type/${type}/requirements` };
      },
      transformResponse: (response) => {
        const { user_details: userDetails, documents, remarks } = response;
        return {
          results: Array(Math.max(userDetails.length, documents.length))
            .fill()
            .map((_, i) => ({
              userDetail: userDetails[i],
              document: documents[i],
            })),
          remarks,
        };
      },
      providesTags: (result, error, { id, type }) => [
        { tag: 'PlanRequirements', id, type },
      ],
    }),
    getReachability: builder.query({
      query: (arg) => {
        const { id, type } = arg;
        return {
          url: `customer/plans/${id}/type/${type}/reachability-services`,
        };
      },
      transformResponse: (response) => {
        const {
          reachability,
          allowed_services: services,
          minutes,
          remarks,
          dial_format,
        } = response;
        return { reachability, services, minutes, remarks, dial_format };
      },
      providesTags: (result, error, { id, type }) => [
        { tag: 'PlanReachabilityServices', id, type },
      ],
    }),
    getOutboundRates: builder.query({
      query: (arg) => {
        const { id, type } = arg;
        return {
          url: `customer/plans/${id}/type/${type}/outbound-rates`,
        };
      },
      transformResponse: (response) => {
        const { name: plan, rates } = response;
        const transformedRates = rates.map(
          ({
            destination,
            call_set_up: callSetup,
            price_min: pricePerMinute,
            currency_traffic: currencyTraffic,
            billing_method: billingMethod,
          }) => ({
            destination,
            callSetup,
            pricePerMinute,
            currencyTraffic,
            billingMethod,
          })
        );
        return { plan, rates: transformedRates };
      },
      providesTags: (result, error, { id, type }) => [
        { tag: 'PlanOutboundRates', id, type },
      ],
    }),
    getPrefixes: builder.query({
      query: ({ id, type, ...params }) => {
        const { prefixName: name, prefix } = params;
        return {
          url: `customer/plans/${id}/type/${type}/prefixes`,
          params: { prefix, name },
        };
      },
      providesTags: (result, error, { id, type }) => [
        { tag: 'PlanPrefixes', type: `${type}-${id}` },
      ],
      transformResponse: (response) => ({
        results: response.map((item) => {
          const tmp = { ...item, isBackorder: false };
          if (item.stock === 0) {
            tmp.isBackorder = true;
          }
          return tmp;
        }),
      }),
    }),
    getPrefixAvailableDids: builder.query({
      query: ({ id, type, prefixID, page, size, ...params }) => ({
        url: `customer/plans/${id}/type/${type}/prefixes/${prefixID}/dids/available`,
        params: { page, page_size: size, ...params },
      }),
      providesTags: (result, error, { id, type, prefixID }) => [
        { tag: 'PlanPrefixAvailableDids', type: `${id}-${prefixID}-${type}` },
      ],
    }),
    getProfilesAgainstRequirements: builder.query({
      query: ({ id, type }) => ({
        url: `customer/plans/${id}/type/${type}/check-profiles`,
      }),
      transformResponse: (response) => {
        const data = response.data.map(
          ({ documents, id, name, valid, blocked }) => {
            const tmp = { id, name, blocked, documents: {}, valid };
            documents.forEach((document) => {
              tmp.documents[document.id] = document.valid;
            });
            return tmp;
          }
        );

        return { fields: response.fields, data };
      },
      providesTags: (result, error, { plan, type }) => [
        { tag: 'PlanProfiles', type: `${plan}-${type}` },
      ],
    }),
    getBackordersInfo: builder.query({
      query: ({ id, type, prefixID }) => ({
        url: `customer/plans/${id}/type/${type}/prefixes/${prefixID}/backorders/available`,
      }),
      providesTags: (result, error, { prefixID }) => [
        { tag: 'PlanBackordersInfo', type: prefixID },
      ],
    }),
    getFieldDescription: builder.query({
      query: () => ({ url: `customer/plan-field-description` }),
      transformResponse: (response) => {
        const data = {};
        if (response.length === 0) {
          return data;
        }
        response
          .filter(({ visible }) => visible)
          .forEach(({ accessor, description }) => {
            data[accessor] = description;
          });
        return data;
      },
      providesTags: ['PlanFieldDescription'],
    }),

    getOutboundServices: builder.query({
      query: (arg) => {
        const { id, type } = arg;
        return {
          url: `customer/plans/${id}/type/${type}/outbound/reachability-services`,
        };
      },
      transformResponse: (response) => {
        const {
          outbound_remark,
          national_fixed,
          national_mobile,
          toll_free,
          call_center_allowed,
        } = response;

        return {
          outbound_remark,
          national_fixed,
          national_mobile,
          toll_free,
          call_center_allowed,
        };
      },
      providesTags: (result, error, { id, type }) => [
        { tag: 'PlanOutboundServices', id, type },
      ],
    }),
  }),
});

export const getSalutationChoices = {
  isUninitialized: false,
  isLoading: false,
  isFetching: false,
  isError: false,
  data: [
    { value: 'mr', name: 'Mr' },
    { value: 'ms', name: 'Ms' },
  ],
};

const initialState = {
  filters: {
    country: [],
    typeOfService: {
      isLoading: false,
      data: [],
    },
    didType: {
      isLoading: false,
      data: [],
    },
  },
  plan: { isLoading: false, data: [], country: null },
  planNumbers: {
    plan: { id: null, country: null, area: null, prefix: null, plan: null },
    typeOfService: [],
    isLoading: false,
    data: [],
  },
  planPrefixes: { plan: { id: null, plan: null }, data: [] },
  planInfo: {
    isLoading: false,
    data: { id: null, country: null, plan: null, details: {} },
    showState: {
      prefixes: false,
      details: false,
      currentPlan: { id: null, type: null, name: '', outboundRates: false },
    },
  },
};
const planSlice = createSlice({
  name: 'plan',
  initialState,
  extraReducers: (builder) => builder,
  reducers: {
    showPlanDetails: (state, action) => {
      state.planInfo.showState.details = true;
      state.planInfo.showState.currentPlan = action.payload;
    },
    hidePlanDetails: (state) => {
      state.planInfo.showState.details = false;
    },
    showPlanPrefixes: (state, action) => {
      state.planInfo.showState.prefixes = true;
      state.planInfo.showState.currentPlan = action.payload;
    },
    hidePlanPrefixes: (state) => {
      state.planInfo.showState.prefixes = false;
    },
    clearPlans: (state) => {
      state.plan = initialState.plan;
      state.planInfo = initialState.planInfo;
    },
    clearPlanInfo: (state) => {
      state.planInfo = initialState.planInfo;
    },
  },
});

export const {
  showPlanDetails,
  hidePlanDetails,
  showPlanPrefixes,
  hidePlanPrefixes,
  clearPlanInfo,
} = planSlice.actions;
export default planSlice.reducer;
